import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Palette mirrors ViewsShared::DicebearAvatars so the inline initials match the server-side
// DiceBear avatars the backend already substitutes for missing photos.
const INITIALS_PALETTE = ['#f7aa9f', '#c7bff9', '#a6bddd', '#f5a5b3'];

function getInitials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function paletteColor(name) {
  const value = name || '';
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 2147483647;
  }
  return INITIALS_PALETTE[hash % INITIALS_PALETTE.length];
}

const ImageCropped = React.forwardRef(
  (
    {
      src,
      alt = '',
      size,
      wrapperClasses = '',
      className = '',
      style = {},
      roundedCircle = false,
      fallbackToInitials = false,
      ...rest
    },
    ref,
  ) => {
    // Opt-in recovery: when the fetch fails, render an inline initials circle in place of the
    // broken img. No second network request, so a flaky CDN can't leave a broken-image artifact.
    const [hasFailed, setHasFailed] = useState(false);

    useEffect(() => {
      setHasFailed(false);
    }, [src]);

    const { onError: userOnError, ...restWithoutOnError } = rest;
    const handleError = (event) => {
      if (fallbackToInitials) setHasFailed(true);
      if (userOnError) userOnError(event);
    };
    const attachError = fallbackToInitials || Boolean(userOnError);

    const wrapperStyle = {
      ...({ width: `${size}px` }),
      ...({ height: `${size}px` }),
      ...style,
    };

    let imgClasses = `object-cover ${className}`;
    if (roundedCircle) { imgClasses += ' rounded-circle'; }

    if (!src && !fallbackToInitials) { return null; }

    const renderInitials = () => (
      <div
        ref={ref}
        className={`d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold ${className}`}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: paletteColor(alt),
          fontFamily: 'sans-serif, Helvetica',
          fontSize: `${size * 0.4}px`,
          lineHeight: 1,
        }}
        aria-label={alt}
        data-testid="image-cropped-initials"
      >
        {getInitials(alt)}
      </div>
    );

    const renderImage = () => React.cloneElement(
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={imgClasses}
        style={{ width: '100%', height: '100%' }}
        onError={attachError ? handleError : undefined}
      />,
      restWithoutOnError,
    );

    return (
      <div className={`d-inline-block ${wrapperClasses}`}>
        <div className="d-inline-block" style={wrapperStyle}>
          {fallbackToInitials && (hasFailed || !src) ? renderInitials() : renderImage()}
        </div>
      </div>
    );
  },
);

ImageCropped.displayName = 'ImageCropped';

ImageCropped.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.number,
  wrapperClasses: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  roundedCircle: PropTypes.bool,
  fallbackToInitials: PropTypes.bool,
};

ImageCropped.defaultProps = {
  src: undefined,
  alt: '',
  size: 40,
  wrapperClasses: '',
  className: '',
  style: {},
  roundedCircle: false,
  fallbackToInitials: false,
};

export default ImageCropped;
